import { useState, forwardRef } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Input, InputProps } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { ControllerRenderProps } from 'react-hook-form'

export type PasswordInputProps = Omit<InputProps, 'type'> & {
  field?: ControllerRenderProps<any, any>
  autoComplete?: string
  transparentBackground?: boolean
}

// Função de força mantida para uso posterior (ex: Zod)
export function getPasswordStrength(password: string) {
  if (!password) return 0
  let strength = 0
  if (password.length >= 8) strength += 1
  if (password.length >= 6) strength += 1
  if (/[A-Z]/.test(password)) strength += 1
  if (/[a-z]/.test(password)) strength += 1
  if (/[0-9]/.test(password)) strength += 1
  if (/[^A-Za-z0-9]/.test(password)) strength += 1
  return Math.min(strength, 5)
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(function PasswordInput(
  {
    field,
    placeholder = 'Digite sua senha',
    className = '',
    autoComplete = 'current-password',
    transparentBackground = false,
    ...props
  },
  ref
) {
  const [showPassword, setShowPassword] = useState(false)

  const togglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  return (
    <div className="relative w-full">
      <Input
        type={showPassword ? 'text' : 'password'}
        placeholder={placeholder}
        autoComplete={autoComplete}
        className={`${transparentBackground ? 'bg-transparent' : 'bg-input'} text-foreground pr-10 ${className}`}
        {...(field || {})}
        {...props}
        ref={ref}
      />
      <Button
        type="button"
        className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
        variant="ghost"
        size="icon"
        onClick={togglePasswordVisibility}
        tabIndex={-1}
      >
        {showPassword ? <Eye className="h-4 w-4 text-primary" /> : <EyeOff className="h-4 w-4 text-primary" />}
        <span className="sr-only">{showPassword ? 'Ocultar senha' : 'Mostrar senha'}</span>
      </Button>
    </div>
  )
})

export default PasswordInput
